import { datadogLogs } from '@datadog/browser-logs';
import { useMutation } from '@tanstack/react-query';
import { AppointmentEntity } from '@web-app/db';
import { useApiClient } from '@web-app/hooks/useApiClient';
import { SAVE_APPOINTMENT_LOG_PREFIX } from '@web-app/utils/api/logger-prefixes';

export const saveAppointment = async (
  data: Partial<AppointmentEntity>,
  apiClient: ReturnType<typeof useApiClient>,
): Promise<AppointmentEntity> => {
  try {
    const response = await apiClient.put(
      `/appointments/${data.dailyRoomName}`,
      data,
    );

    return response.data as AppointmentEntity;
  } catch (error) {
    datadogLogs.logger.error(
      `${SAVE_APPOINTMENT_LOG_PREFIX} Error saving appointment`,
      {
        dailyRoomName: data.dailyRoomName,
        healthieClientId: data.healthieClientId,
        healthieProviderId: data.healthieProviderId,
        err:
          error instanceof Error
            ? {
                message: error.message,
                stack: error.stack,
              }
            : String(error),
      },
    );
    throw error;
  }
};

export const useSaveAppointment = ({
  onSuccess,
}: {
  onSuccess: (data: AppointmentEntity) => void;
}) => {
  const apiClient = useApiClient();

  return useMutation<AppointmentEntity, Error, Partial<AppointmentEntity>>({
    mutationKey: ['berrystreetSaveAppointmentV3'],
    mutationFn: (data: Partial<AppointmentEntity>) =>
      saveAppointment(data, apiClient),
    onSuccess: (data: AppointmentEntity) => {
      datadogLogs.logger.info(
        `${SAVE_APPOINTMENT_LOG_PREFIX} Appointment saved successfully`,
        {
          dailyRoomName: data.dailyRoomName,
          healthieClientId: data.healthieClientId,
          healthieProviderId: data.healthieProviderId,
          appointmentId: data.id,
        },
      );
      onSuccess?.(data);
    },
    onError: (error: unknown, variables: Partial<AppointmentEntity>) => {
      datadogLogs.logger.error(
        `${SAVE_APPOINTMENT_LOG_PREFIX} Failed to save appointment after retries`,
        {
          dailyRoomName: variables.dailyRoomName,
          healthieClientId: variables.healthieClientId,
          healthieProviderId: variables.healthieProviderId,
          err:
            error instanceof Error
              ? {
                  message: error.message,
                  stack: error.stack,
                }
              : String(error),
        },
      );
    },
    retry: 2,
    retryDelay: 2000,
  });
};
